import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    StyledMiddleEllipsisParent,
    StyledMiddleEllipsisText,
    StyledMiddleEllipsisWrapper
} from 'src/ui/common/molecules/middle-ellipsis/middle-ellipsis.style';
import { useWindowListener } from 'src/hooks/use-window-listener';
import { useIsMounted } from 'src/hooks/useIsMounted';

const MiddleEllipsis = ({ text }: { text: string }) => {
    const [ellipsisText, setEllipsisText] = useState(text);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const parentRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<HTMLSpanElement>(null);
    const [componentMounted, setIsMounted] = useState(false);
    const isMounted = useIsMounted();

    const wrapper = wrapperRef.current;
    const parent = parentRef.current;
    const txtNode = textRef.current;

    const calcTextDimension = useCallback(() => {
        isMounted(() => {
            setEllipsisText(text);
            if (!wrapper || !parent || !txtNode) {
                return;
            }
            const nodeWithSmallerWidth = wrapper.offsetWidth > parent!.offsetWidth ? parent : wrapper;
            const containerWidth = nodeWithSmallerWidth!.offsetWidth;
            const txtWidth = txtNode!.offsetWidth;
            const targetWidth = txtWidth;

            if (targetWidth > containerWidth) {
                const str = text;
                const txtChars = str.length;
                const avgLetterSize = txtWidth / txtChars;
                const canFit = (containerWidth - (targetWidth - txtWidth)) / avgLetterSize;
                const delEachSide = (txtChars - canFit + 5) / 2;
                const endLeft = Math.floor(txtChars / 2 - delEachSide);
                const startRight = Math.ceil(txtChars / 2 + delEachSide);
                setEllipsisText(`${str.substring(0, endLeft)  } ... ${  str.substring(startRight)}`);
            }
        });
    }, [isMounted, parent, txtNode, wrapper, text]);

    useWindowListener('resize', calcTextDimension, 200);

    useEffect(() => {
        setTimeout(calcTextDimension, 0);
    }, [componentMounted, calcTextDimension]);

    useEffect(() => {
        setIsMounted(true);
        return () => setIsMounted(false);
    }, []);

    return (
        <StyledMiddleEllipsisWrapper ref={wrapperRef}>
            <StyledMiddleEllipsisParent ref={parentRef}>
                <StyledMiddleEllipsisText ref={textRef}>
                    {ellipsisText}
                </StyledMiddleEllipsisText>
            </StyledMiddleEllipsisParent>
        </StyledMiddleEllipsisWrapper>
    );
};

export default MiddleEllipsis;